<script setup lang="ts">
import { useElementSize } from '@vueuse/core'
import { Modal } from 'bootstrap'
import { OverlayScrollbars } from 'overlayscrollbars'
import { storeToRefs } from 'pinia'
import { useScopeId } from '@/composables/use-scoped-id'
import { useLoadingStore } from '@/stores/use-loading-store'
import type { ClassValue } from '~library/types'
import { waitNextTick } from '~library/utils/functions'
import type { ModalInstance } from './types'

interface Props {
  ariaDescribedby?: string
  ariaLabelledBy?: string
  centered?: boolean
  enableTransform?: boolean
  fade?: boolean
  focus?: boolean
  message?: string
  modalTitle?: string
  modalClass?: string
  modalDialogClass?: string
  modalBodyClass?: ClassValue
  modalBodyRefSelector?: string
  keyboard?: boolean
  modalFooterClass?: ClassValue
  scrollable?: boolean
  size?: 'sm' | 'md' | 'lg' | 'xl'
  onlyBody?: boolean
  closeable?: boolean
  beforeHide?: any
  showSpinners?: boolean
  loaderId?: string
}

const modelValue = defineModel({
  type: Boolean,
  required: true
})

const props = withDefaults(defineProps<Props>(), {
  ariaDescribedby: undefined,
  ariaLabelledBy: undefined,
  centered: true,
  enableTransform: true,
  fade: true,
  focus: true,
  message: '',
  modalTitle: '',
  modalClass: '',
  modalDialogClass: '',
  modalBodyClass: '',
  modalBodyRefSelector: undefined,
  keyboard: undefined,
  modalFooterClass: '',
  scrollable: true,
  size: 'md',
  onlyBody: false,
  closeable: true,
  beforeHide: null,
  showSpinners: false,
  loaderId: ''
})

const emits = defineEmits<{
  confirm: []
  cancel: []
  'modal-hide': []
  'modal-hidden': []
  'modal-show': []
  'modal-shown': []
}>()

const slots = defineSlots<{
  default?(): void
  modalTitle?(): void
  footer?(): void
}>()

/** modal 內部使用的變數，用來判斷 modal 是開啟或關閉 */
const isModalShow = ref<boolean>(false)

/** modal 是否已開啟 - 每次開啟 modal 時需重新渲染內容，避免每次開啟的高度不同時，底部會有多餘空白 */
const isModalShown = ref(false)

const modalRef = useTemplateRef('modalRef')
const modalHeaderRef = useTemplateRef('modalHeaderRef')
const modalBodyRef = useTemplateRef('modalBodyRef')
const modalFooterRef = useTemplateRef('modalFooterRef')

const { isAreaLoading } = storeToRefs(useLoadingStore())

const { parentsScopedIdAttribute } = useScopeId()

const modalOption = computed((): Modal.Options => {
  return {
    backdrop: modalBackdropValue.value,
    keyboard: modalKeyboardValue.value,
    focus: props.focus
  }
})

const modal = ref<ModalInstance | null>(null)

const { height: headerHeight } = useElementSize(modalHeaderRef)
const { height: footerHeight } = useElementSize(modalFooterRef)

const modalBackdropValue = computed(() => {
  return modalKeyboardValue.value ? true : 'static'
})

const modalKeyboardValue = computed(() => {
  return (
    (props.keyboard ?? props.closeable) && !isAreaLoading.value[props.loaderId]
  )
})

function modalHide() {
  modal.value?.hide()
}

function modalShow() {
  modal.value?.show()
}

async function hide() {
  if (!props.beforeHide || (await props.beforeHide())) {
    modelValue.value = false
  }
}

function handleCancel() {
  hide()
  emits('cancel')
}

function modalShowEvent() {
  // 同步彈窗狀態
  isModalShow.value = true
  emits('modal-show')
}

async function modalShownEvent() {
  if (!modelValue.value && !isModalShow.value) {
    modalHide()
  } else {
    modelValue.value = true
    isModalShown.value = true
    await waitNextTick()
    setModalOverlayScrollbars()
    emits('modal-shown')
  }
}

function modalHideEvent() {
  // 點擊 ESC 或彈窗外部關閉彈窗時，同步彈窗狀態
  isModalShow.value = false
  emits('modal-hide')
}

function modalHiddenEvent() {
  if (modelValue.value && isModalShow.value) {
    modalShow()
  } else {
    modelValue.value = false
    emits('modal-hidden')
    isModalShown.value = false
  }
}

function setModalOverlayScrollbars() {
  const modalBodyRef = getModalBodyRef()

  if (!modalBodyRef) {
    return
  }

  OverlayScrollbars(
    {
      target: modalBodyRef,
      elements: {
        viewport: modalBodyRef
      }
    },
    {
      overflow: {
        x: 'hidden'
      },
      scrollbars: {
        visibility: 'visible',
        theme: 'os-theme-custom'
      }
    }
  )
}

// 監聽 modeValue - 開啟/關閉 Modal
watch(
  () => modelValue.value,
  (value: boolean) => {
    // 同步彈窗狀態
    isModalShow.value = value

    if (value) {
      modalShow()
    } else {
      modalHide()
    }
  }
)

// modal 選項更新時，同步 bootstrap 的設定
watch(
  () => modalOption.value,
  (value) => {
    if (modal.value) {
      modal.value._config = value
    }
  },
  {
    deep: true
  }
)

function getModalBodyRef(): HTMLElement | null {
  if (props.modalBodyRefSelector) {
    return document.querySelector(props.modalBodyRefSelector)
  }

  return modalBodyRef.value
}

onMounted(() => {
  if (modalRef.value) {
    modal.value = new Modal(modalRef.value, modalOption.value)

    modalRef.value.addEventListener('hide.bs.modal', modalHideEvent)
    modalRef.value.addEventListener('hidden.bs.modal', modalHiddenEvent)
    modalRef.value.addEventListener('show.bs.modal', modalShowEvent)
    modalRef.value.addEventListener('shown.bs.modal', modalShownEvent)

    if (modelValue.value) {
      modal.value.show()
    }
  }
})

onBeforeUnmount(() => {
  modalRef.value?.addEventListener('hide.bs.modal', modalHideEvent)
  modalRef.value?.removeEventListener('hidden.bs.modal', modalHiddenEvent)
  modalRef.value?.removeEventListener('show.bs.modal', modalShowEvent)
  modalRef.value?.removeEventListener('shown.bs.modal', modalShownEvent)
})
</script>

<template>
  <teleport to="body">
    <div v-bind="parentsScopedIdAttribute">
      <div
        ref="modalRef"
        v-bind="parentsScopedIdAttribute"
        aria-hidden="true"
        class="modal modal-standard"
        tabindex="-1"
        :aria-describedby="props.ariaDescribedby"
        :aria-labelledby="props.ariaLabelledBy"
        :class="{
          fade: props.fade,
          [`modal-${props.size}`]: true,
          [props.modalClass]: true
        }"
        :data-bs-backdrop="modalBackdropValue"
        :data-bs-focus="props.focus"
        :data-bs-keyboard="modalKeyboardValue"
      >
        <div
          class="modal-dialog"
          tabindex="-1"
          :class="{
            'modal-dialog-centered': props.centered,
            'modal-dialog-scrollable': props.scrollable,
            [props.modalDialogClass]: true
          }"
          :style="props.enableTransform ? [] : 'transform: none'"
        >
          <div
            class="modal-content position-relative"
            :class="[props.onlyBody ? 'p-0' : 'gap-12 gap-md-16']"
          >
            <div
              v-if="!props.onlyBody"
              ref="modalHeaderRef"
              class="modal-header border-0 mx-auto"
            >
              <h4
                class="modal-title headline-4"
                :class="modelValue ? 'd-block' : ''"
              >
                {{ props.modalTitle }}
              </h4>
              <div
                class="modal-title-element"
                :class="slots.modalTitle ? 'd-block' : ''"
              >
                <slot name="modalTitle"></slot>
              </div>
              <button
                v-if="props.closeable"
                class="btn-close"
                type="button"
                :aria-label="$t('Common.AppModal.ClosePopup')"
                :disabled="isAreaLoading[props.loaderId]"
                @click="handleCancel"
              >
                <svg aria-hidden="true" class="icon icon-close">
                  <use xlink:href="#Close" />
                </svg>
              </button>
            </div>
            <div
              ref="modalBodyRef"
              class="modal-body"
              :class="props.modalBodyClass"
            >
              <template v-if="isModalShown">
                <!-- 只顯示 modal body -->
                <template v-if="props.onlyBody">
                  <slot></slot>
                </template>

                <template v-else>
                  <div
                    v-if="message"
                    class="modal-message text--lg text-center"
                    :class="message ? 'd-block' : ''"
                  >
                    {{ message }}
                  </div>

                  <div
                    class="modal-message-element"
                    :class="slots.default ? 'd-block' : ''"
                  >
                    <slot></slot>
                  </div>
                </template>
              </template>
            </div>

            <!-- Loader -->
            <app-loader
              v-if="isAreaLoading[props.loaderId]"
              style="padding-top: inherit; padding-bottom: inherit"
              :style="[
                { top: `${headerHeight}px`, bottom: `${footerHeight}px` }
              ]"
            />

            <div
              v-if="slots.footer"
              ref="modalFooterRef"
              :class="modalFooterClass"
            >
              <slot name="footer"></slot>
            </div>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>
